import Footer from '~/components/Footer'
import GetStarted from '~/components/GetStarted'
import Navigation from '~/components/Navigation'
import useTranslator from '~/lib/useTranslator'
import { mergeMeta } from '~/utils/mergeMeta'

export const meta = mergeMeta(() => [{ title: 'Heja | About Heja' }])

export default function About() {
  const Translator = useTranslator()

  return (
    <>
      <Navigation />

      <section className="bg-primary-color px-4">
        <div className="lg:grid grid-cols-2 gap-12 max-w-5xl mx-auto py-24">
          <p className="text-4xl font-bold leading-snug lg:leading-relaxed text-white">
            {Translator.singular(
              'We are firm believers of the positive impact sports can play in the lives of kids and youths, for generations to come.'
            )}
          </p>

          <p className="text-white lg:block hidden text-2xl font-thin leading-relaxed">
            {Translator.singular(
              'Sports should be fun and encouraging and are a place for languages and cultures to meet. Sports are universal and translate to every part of the world, where anyone can partake, practice to get better, and be part of a team story. That’s the beauty of it, don’t you think?'
            )}
          </p>
        </div>
      </section>

      <section className="bg-gradient-to-b from-primary-color from-50% to-50% to-white px-4">
        <div className="max-w-screen-xl lg:mx-auto bg-[#339463] rounded-lg shadow-lg">
          <div className="flex flex-col lg:grid lg:h-full lg:grid-cols-3 text-white">
            <div className="flex flex-col items-center justify-center border-b border-white/10 lg:border-r lg:border-b-0 py-10 px-10">
              <h5 className="text-2xl font-medium">
                {Translator.singular('{{ noOfTeams }} Teams', { noOfTeams: '300,000+' })}
              </h5>
              <p>{Translator.singular('use Heja with their team.')}</p>
            </div>

            <div className="flex flex-col items-center justify-center border-b border-white/10 lg:border-r lg:border-b-0 py-10 px-10">
              <h5 className="text-2xl font-medium">
                {Translator.singular('15+ years of experience')}
              </h5>
              <p>{Translator.singular('in building tech for youth sports.')}</p>
            </div>

            <div className="flex flex-col items-center justify-center py-10 px-10">
              <h5 className="text-2xl font-medium">{Translator.singular('50+ Countries')}</h5>
              <p>{Translator.singular('and growing.')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white pt-12 px-4">
        <div className="max-w-5xl mx-auto flex flex-1 flex-col-reverse lg:flex-row items-center gap-14">
          <div className="flex flex-1 flex-col gap-4">
            <h2 className='font-bold'>{Translator.singular('Our Company')}</h2>
            <p className="text-xl">
              {Translator.singular(
                'We want to make it possible for every child in the world to experience the joys of team sports. From building friendships to bridging cultures and boosting health. That’s what we believe in. Through Heja, we make it simple and accessible for everyone - including coaches, families and players - to be part of a well-run sports team.'
              )}
            </p>
          </div>
          <img src="/images/company.jpg" width={454} height={454} alt="Heja company in a meeting" />
        </div>
      </section>

      <section className="bg-white px-4 pt-12">
        <div className="max-w-screen-md mx-auto">
          <h4 className="text-3xl leading-normal font-bold text-center">
            {Translator.singular(
              'Before Heja, our founders started a niche team sports tool that reached +2M users and are now building something accessible for every team worldwide.'
            )}
          </h4>
        </div>
      </section>

      <section className="bg-white px-4 pt-24">
        <div className="max-w-5xl mx-auto flex flex-1 flex-col lg:flex-row items-center gap-14">
          <img src="/images/our-team.jpg" width={454} height={454} alt="Typing on keyboard" />
          <div className="flex flex-1 flex-col gap-4">
            <h2 className='font-bold'>{Translator.singular('Our Team')}</h2>
            <p className="text-xl">
              {Translator.singular(
                'Just like any great sports team, we’re a close group with a diverse mix of backgrounds and skills. Experienced developers, talented designers and leaders with a long history in digital communications, we value simplicity, privacy, listening to user feedback and delivering great customer support. Everyone works towards the same goal: to create a home where sports teams from all over the world can grow together.'
              )}
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 pt-24">
        <div className="max-w-5xl mx-auto flex flex-1 flex-col-reverse lg:flex-row items-center gap-14">
          <div className="flex flex-1 flex-col gap-4 w-full">
            <a id="press" href="#press" aria-hidden>
              <h2 className='font-bold'>{Translator.singular('Press')}</h2>
            </a>
            <p>
              {Translator.singular('Press related contact:')}
              <br />
              <span className="font-normal">Mikael, CEO: mikael@heja.io</span>
            </p>

            <p className="font-normal">{Translator.singular('Downloadable assets:')}</p>

            <table>
              <tbody>
                <tr>
                  <td>
                    <img
                      src="/images/press-heja-Johan_Berglund_(left)_&_Mikael_Blix_(right).jpg"
                      width={50}
                      height={50}
                      alt="Heja founders"
                    />
                  </td>
                  <td>
                    <div>
                      Founder Photo |{' '}
                      <a
                        href="/images/press-heja-Johan_Berglund_(left)_&_Mikael_Blix_(right).jpg"
                        className="underline"
                        download
                      >
                        {Translator.singular('Download')}
                      </a>
                    </div>
                    <div className="text-xs">
                      {Translator.singular(
                        'Founder Photo - Johan Berglund (left) & Mikael Blix (right)'
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <img src="/images/product_heja.png" width={50} height={50} alt="Product" />
                  </td>
                  <td>
                    {Translator.singular('Product Photo')} |{' '}
                    <a href="/images/product_heja.png" className="underline" download>
                      Download
                    </a>
                  </td>
                </tr>
                <tr>
                  <td>
                    <img
                      src="/images/logo_green.png"
                      width={50}
                      height={50}
                      alt="Original logo (green)"
                    />
                  </td>
                  <td>
                    {Translator.singular('Original logo (green)')} |{' '}
                    <a href="/images/logo_green.png" className="underline" download>
                      Download
                    </a>
                  </td>
                </tr>
                <tr>
                  <td>
                    <img src="/images/logo_white.png" width={50} height={50} alt="White logo" />
                  </td>
                  <td>
                    {Translator.singular('White logo')} |{' '}
                    <a href="/images/logo_white.png" className="underline" download>
                      Download
                    </a>
                  </td>
                </tr>
                <tr>
                  <td>
                    <img src="/images/logo_black.png" width={50} height={50} alt="Black logo" />
                  </td>
                  <td>
                    {Translator.singular('Black logo')} |{' '}
                    <a href="/images/logo_black.png" className="underline" download>
                      Download
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <img src="/images/press.jpg" width={454} height={454} alt="Heja founders" />
        </div>
      </section>

      <section className="bg-gradient-to-b from-white from-50% to-transparent to-50% px-4 pt-12 pb-24">
        <div className="w-full max-w-5xl mx-auto">
          <GetStarted />
        </div>
      </section>

      <Footer />
    </>
  )
}
